
import { useState } from 'react';
import { useForm, ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  MacroCurriculumFormValues, 
  macroCurriculumSchema, 
  beltColors 
} from './schema';
import { MacroCurriculum } from '@/services/roadmap/roadmapService';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { RichTextEditor } from '../shared/RichTextEditor';

interface MacroCurriculumFormProps {
  mode?: 'create' | 'edit';
  initialData?: MacroCurriculum;
  onSubmit?: (data: MacroCurriculumFormValues) => Promise<void>;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export function MacroCurriculumForm({ 
  mode = 'create',
  initialData, 
  onSubmit,
  onCancel,
  onSuccess
}: MacroCurriculumFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const defaultValues: Partial<MacroCurriculumFormValues> = {
    title: initialData?.title || '',
    belt_color: initialData?.belt_color || 'White',
    stripe_count: initialData?.stripe_count || 0,
    content: initialData?.content || '',
    image_url: initialData?.image_url || '',
    video_url: initialData?.video_url || '',
    order_index: initialData?.order_index || 0,
    is_active: initialData?.is_active ?? true,
  };

  const form = useForm<MacroCurriculumFormValues>({
    resolver: zodResolver(macroCurriculumSchema),
    defaultValues,
  });

  const handleSubmit = async (data: MacroCurriculumFormValues) => {
    try {
      setIsSubmitting(true);
      
      if (onSubmit) {
        await onSubmit(data);
      }
      
      toast({
        title: mode === 'edit' ? 'Curriculum Updated' : 'Curriculum Created',
        description: `Successfully ${mode === 'edit' ? 'updated' : 'created'} curriculum entry.`,
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save curriculum. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Title */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'title'> }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter curriculum title" {...field} />
                </FormControl>
                <FormDescription>
                  The title for this curriculum entry
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Order Index */}
          <FormField
            control={form.control}
            name="order_index"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'order_index'> }) => (
              <FormItem>
                <FormLabel>Display Order</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormDescription>
                  Controls the display order (lower numbers appear first)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Belt Color */}
          <FormField
            control={form.control}
            name="belt_color"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'belt_color'> }) => (
              <FormItem>
                <FormLabel>Belt Color</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a belt color" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {beltColors.map((belt) => (
                      <SelectItem key={belt.value} value={belt.value}>
                        {belt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  The corresponding belt rank
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Stripe Count */}
          <FormField
            control={form.control}
            name="stripe_count"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'stripe_count'> }) => (
              <FormItem>
                <FormLabel>Stripe Count</FormLabel>
                <Select 
                  onValueChange={(value) => field.onChange(Number(value))} 
                  defaultValue={String(field.value)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stripe count" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {[0, 1, 2, 3, 4].map((count) => (
                      <SelectItem key={count} value={String(count)}>
                        {count}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Number of stripes (0-4)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Image URL */}
          <FormField
            control={form.control}
            name="image_url"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'image_url'> }) => (
              <FormItem>
                <FormLabel>Image URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://example.com/image.jpg" {...field} />
                </FormControl>
                <FormDescription>
                  Optional image URL
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Video URL */}
          <FormField
            control={form.control}
            name="video_url"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'video_url'> }) => (
              <FormItem>
                <FormLabel>Video URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://youtube.com/watch?v=..." {...field} />
                </FormControl>
                <FormDescription>
                  Optional video URL (YouTube)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Is Active */}
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }: { field: ControllerRenderProps<MacroCurriculumFormValues, 'is_active'> }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active Status</FormLabel>
                  <FormDescription>
                    Whether this curriculum entry should be visible
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        
        {/* Content */}
        <RichTextEditor
          form={form}
          name="content"
          label="Content"
          description="Curriculum details in markdown format. Add comprehensive descriptions, examples, and explanations."
          placeholder="# Main Section Title
## Subsection
Key concepts to understand:
- Point 1
- Point 2

## Another Subsection
More detailed explanation..."
        />

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initialData ? 'Update' : 'Create'} Curriculum
          </Button>
        </div>
      </form>
    </Form>
  );
}
